/**
 * Q&A の追加・編集(admin)。カテゴリは既存の名前から選べる(新しい名前も入れられる)。
 * 保存していない変更があるまま閉じるときは確認を出す。削除は取り消せないため確認してから行い、中身は監査ログに残す
 */
import { useState } from 'react';
import { useCurrentUser } from '@core/auth/AuthProvider';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { createQa, deleteQa, updateQa, type QaItem } from './qaRepo';

export default function QaEditModal({ item, categories, nextOrder, onClose, onSaved }: { item: QaItem | null; categories: string[]; nextOrder: number; onClose: () => void; onSaved: () => void }) {
  const user = useCurrentUser();
  const toast = useToast();
  const initial = { category: item?.category ?? '', question: item?.question ?? '', answer: item?.answer ?? '', order: String(item?.sort_order ?? nextOrder) };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const requestClose = () => (dirty ? setConfirmClose(true) : onClose());

  async function save() {
    const order = Number(form.order.trim());
    if (form.order.trim() === '' || !Number.isInteger(order) || order < 0) {
      toast('表示順は0以上の整数にしてください');
      return;
    }
    setBusy(true);
    try {
      const input = { category: form.category, question: form.question, answer: form.answer, sort_order: order };
      if (item) await updateQa(item, input, user.id);
      else await createQa(input, user.id);
      toast(item ? '保存しました' : '追加しました');
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!item || !confirm(`「${item.question}」を削除します。元に戻せません。よろしいですか?`)) return;
    setBusy(true);
    try {
      await deleteQa(item, user.id);
      toast('削除しました');
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? e.message : '削除に失敗しました');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={requestClose} width="min(720px, 94vw)">
      <h3>{item ? 'Q&Aの編集' : 'Q&Aの追加'}</h3>
      <div className="form-grid" style={{ marginTop: 12 }}>
        <div>
          <label>カテゴリ *</label>
          <input value={form.category} onChange={(e) => set({ category: e.target.value })} list="qa-categories" autoFocus={!item} />
          <datalist id="qa-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div>
          <label>表示順(小さいほど上)*</label>
          <input value={form.order} onChange={(e) => set({ order: e.target.value })} inputMode="numeric" />
        </div>
        <div className="fw">
          <label>質問 *</label>
          <textarea rows={2} value={form.question} onChange={(e) => set({ question: e.target.value })} />
        </div>
        <div className="fw">
          <label>回答 *</label>
          <textarea rows={8} value={form.answer} onChange={(e) => set({ answer: e.target.value })} />
        </div>
      </div>
      <div className="modal-actions">
        <button className="btn-green" onClick={() => void save()} disabled={busy || !dirty}>
          {item ? '保存する' : '追加する'}
        </button>
        <button className="btn-line" onClick={requestClose} disabled={busy}>
          閉じる
        </button>
        {item && (
          <button className="btn-line danger" onClick={() => void remove()} disabled={busy}>
            削除
          </button>
        )}
      </div>
      {confirmClose && (
        <Modal onClose={() => setConfirmClose(false)} width="min(460px, 94vw)" showClose={false}>
          <h3>保存されていない変更があります</h3>
          <p style={{ margin: '8px 0 0' }}>このまま閉じると、入力した内容は失われます。</p>
          <div className="modal-actions center">
            <button className="btn-green" onClick={() => setConfirmClose(false)}>
              入力に戻る
            </button>
            <button className="btn-line danger" onClick={onClose}>
              保存せずに閉じる
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
